<template>
  <div class="container">
    <div class="product-hero c-px-2 c-md:c-px-0 row c-lg:block clearfix c-justify-center">
      <!-- Name and description -->
      <div class="col-12 c-xl:col-6 c-float-right order-0 relative c-mb-4 c-xl:c-mb-0">
        <header class="c-pb-1 text-center c-xl:text-left">
          <div class="product-breadcrumb">
            <BreadCrumb
              :breadcrumb-list="breadcrumbList"
              additional-class-name="c-justify-center c-xl:c-justify-start c-lg:c-pl-7 c-xl:c-pl-0"
            />
          </div>
          <h1 class="product-hero__title heading-4 c-mb-2 c-md:c-mb-1">
            {{ product.name }}
          </h1>
          <div class="flex c-items-center c-justify-center c-xl:c-justify-start">
            <ProductStarsRating
              v-if="product.starRating && parseInt(product.starRating) > 0"
              :rating="product.starRating"
            />
            <div
              v-if="product.commentsSum > 0"
              class="to-comments underline cursor-pointer"
              @click="scrollToTabControl(3)"
            >
              {{ product.commentsSum }} {{ ratingLabel }}
            </div>
          </div>
          <p
            v-if="product.positives && product.positives.length > 0"
            class="c-mb-0 c-mt-2 text-sm-c"
            v-html="product.positives"
          />
        </header>
      </div>
      <!-- Image (gallery) -->
      <div class="col-12 c-md:col-6 c-xl:c-pt-5 c-float-left order-1">
        <div class="relative c-px-1 c-md:c-pl-0 c-md:c-pr-4 c-lg:ml-n6">
          <ProductItemToast
            :toast-operation="toastObject"
          />
          <div class="product-hero__bubble product-hero__bubble--top pattern-2 hidden c-lg:block" />
          <div class="product-hero__bubble product-hero__bubble--bottom pattern-1 hidden c-lg:block" />
          <DiscountBubble
            v-if="product.hasDiscount || (product.plusClubPrice !== null && getCurrentCustomer?.inPlusClub)"
            :price-with-discount="parseFloat((product.plusClubPrice !== null && getCurrentCustomer?.inPlusClub) ? product.plusClubPrice : product.price)"
            :price-without-discount="parseFloat(product.priceWithoutDiscount)"
            :currency="product.currency"
            class="c-lg:c-mt-6 c-lg:c-ml-6"
          />
          <!-- Carousel -->
          <div class="product-carousel__wrapper">
            <ProductCarousel :images="sortedProductImages" :product-title="product.name" />
          </div>
          <!-- Add to favorite, like, new -->
          <ul
            class="product-hero__tags flex c-items-center c-flex-col absolute top-0 right-0 list-unstyled c-pt-6 c-lg:c-pt-0 c-xl:c-pt-2 c-lg:c-mt-7 mr-n1 c-lg:c-mr-5"
          >
            <li
              v-if="isRecommendedProduct"
              class="c-lg:c-mb-1 flex c-flex-col text-success"
            >
              <i class="mi-recommended icon-36" />
            </li>
            <li
              v-if="isNewProduct"
              class="flex c-flex-col text-danger"
            >
              <i class="mi-new icon-36" />
            </li>
          </ul>
        </div>
      </div>
      <!-- Other information -->
      <div
        v-if="product.categories[1].systemName !== 'ready_order_gifts'"
        class="col-12 c-md:col-8 c-lg:col-6 c-mx-auto c-float-right order-2 c-md:order-3 c-mt-3"
      >
        <!-- Line for mobile and tablet -->
        <hr class="c-my-5 hidden c-md:block c-lg:hidden border-primary opacity-20">
        <!-- Product variants -->
        <div
          v-if="productVariants && productVariants.length > 0"
          class="w-100 c-mb-5 order-1 c-px-1 product-variants"
        >
          <div class="block c-lg:inline-block">
            <ProductVariantsDropdown :products="productVariants" />
          </div>
        </div>
        <!-- Price, weight, tags -->
        <div
          class="row c-items-start mt-n3 c-mb-4 c-pt-3 c-md:c-pt-0"
          :class="{ 'mt-4 c-md:c-mt-5': !(product.variants && product.variants.productVariants.length > 0) }"
        >
          <div class="col-12">
            <strong
              v-if="depositProduct"
              class="c-mt-1 block text-sm"
            >
              <span class="mr-2 ml-1">♻️</span> {{ t('packaging_deposit') }}
            </strong>
            <ProductEstimatedTransport
              v-if="product.expectedDeliveryDate || product.carrierHandoverDate || product.immediateDelivery"
              :expected-delivery-date="product.expectedDeliveryDate"
              :carrier-handover-date="product.carrierHandoverDate"
              :immediate-delivery="product.immediateDelivery"
              :class="{ 'mt-2': depositProduct }"
            />
          </div>
          <!-- Price, weight -->
          <div
            v-if="!isHiddenProduct"
            class="col-auto order-0 c-mt-3 c-xl:c-mr-4"
          >
            <p class="flex c-mb-0 whitespace-nowrap">
              <strong class="h2 block c-mb-0 c-mr-1 font-family-base">
                <span
                  :class="{
                    'line-through text-danger': product.plusClubPrice !== null && getCurrentCustomer?.inPlusClub,
                  }"
                >
                  <ProductPrice
                    :price-first="product.price"
                    :currency="product.currency"
                  />
                </span>
                <span v-if="product.weight > 0 || product.volue > 0"> /</span>
              </strong>
              <span
                v-if="product.weight > 0 || product.volume > 0"
                class="h2 block c-m-0 font-family-base font-normal text-muted"
              >{{ product.unit === 'g' ? product.weight : product.volume }} {{ product.unit }}</span>
              <span v-if="product.hasDiscount && (product.productPack && product.productPack.products.length > 0)">
                <button
                  v-tooltip="{
                    content: t('pack_discount_tooltip'),
                    theme: 'checkoutTooltip',
                  }"
                  class="btn btn-outline-warning c-p-1 c-ml-2 c-mt-0 c-sm:c-mt-1 c-rounded-circle"
                  type="button"
                  aria-label="info"
                  @click.prevent
                >
                  <i class="mi-question icon-12 mi-no-mx mi-lh-1" />
                </button>
              </span>
            </p>
            <p class="c-mb-0">
              <strong
                v-if="product.hasDiscount && (product.plusClubPrice === null || !getCurrentCustomer?.inPlusClub)"
                class="line-through text-danger h3 c-mb-0 c-mr-3 c-lg:c-mr-4 font-family-base"
              >
                <ProductPrice
                  :price-first="product.priceWithoutDiscount"
                  :currency="product.currency"
                />
              </strong>
              <span
                v-if="product.hasDiscount && product.plusClubPrice !== null && !product.price"
                class="h3 font-family-base whitespace-nowrap text-muted"
              >
                / {{ product.weight }} g
              </span>
            </p>
            <p
              v-if="product.pricePerPortion"
              class="small c-mb-0"
            >
              ({{ numberToCurrency(parseFloat(product.pricePerPortion).toFixed(2), product.currency) }} / 100 {{ product.unit }})
            </p>
          </div>
          <!-- Adds to cart, adds, removes, share -->
          <div class="form-row product-buttons c-mt-3 order-2 c-xl:order-1 mr-l c-xl:c-ml-0">
            <!-- Adds to cart -->
            <div
              v-if="product.isReadyProduct || product.isGift"
              class="col c-lg:col-auto"
            >
              <AddToCartButton
                v-if="product.state === 'available'"
                :show-decrement-button="true"
                :product="product"
                :wide="true"
                theme="transparent"
                :odometer-fill="true"
                @cart-operation="createToast"
              />
              <ButtonState
                v-else
                :state="product.state"
                :product="product"
                added-class="w-100"
              />
            </div>
            <div
              v-else-if="product.isIngredient"
              class="col c-lg:col-auto"
            >
              <PushToMixButton
                v-if="product.state === 'available'"
                :product="product"
                added-class="w-100"
              />
              <ButtonState
                v-else
                :state="product.state"
                :product="product"
                added-class="w-100"
              />
            </div>
            <!-- Adds to favorite -->
            <div class="col-auto flex c-m-0 relative z-2">
              <Favorite
                v-if="!product.isIngredient"
                :id="product.id"
                box-class="c-m-0"
                btn-class="btn  btn-info stretched-link c-rounded-pill border-0 c-p-2 relative"
                icon-size="28"
                icon-color-class="text-white"
              />
              <ButtonShare
                v-if="!isHiddenProduct"
                :link="productShowLink"
                class="col-auto"
                :icon-only="true"
              />
            </div>
          </div>
          <div
            v-if="product.plusClubPrice"
            class="flex w-100 c-items-center c-mt-2 order-1 c-xl:order-2 col-auto c-flex-wrap"
          >
            <p class="c-mb-1">
              <strong class="h2 block c-mb-0 c-mr-2 font-family-base">
                <ProductPrice
                  :price-first="product.plusClubPrice"
                  :currency="product.currency"
                />
              </strong>
            </p>
            <MixitPlusTagSvg class="c-mr-2" />
            <div class="col-auto c-px-0 mixit-plus-flex">
              <router-link
                :to="{ name: 'productPath', params: { permalink: t('voucher_permalink') } }"
                class="flex c-justify-start c-items-center c-flex-nowrap c-mb-1 !no-underline"
              >
                <span class="flex border border-warning border-2 text-warning c-p-1 c-rounded-circle">
                  <i class="mi-question icon-12" />
                </span>
                <span class="text-sm-c whitespace-nowrap c-ml-1">{{ t('for_mixit_plus') }}</span>
                <MixitPlusLogoSvg
                  width="94"
                  class="c-ml-1 relative c-mt-1"
                />
              </router-link>
            </div>
          </div>
        </div>
        <!-- Tags -->
        <ProductPropertiesList
          :properties="filteredProperties"
          class="col-auto c-lg:col c-pt-0 c-pl-0"
        />
      </div>
      <!-- Quantity discount progress -->
      <QuantityDiscountProgress
        v-if="showDiscountProgress()"
        :product="product"
      />
      <div>
        <!-- Description + ean -->
        <div class="col-12 c-md:col-6 c-mb-0 c-lg:c-mb-5 c-float-right order-3 c-md:order-2">
          <div class="text-sm-c c-mt-6 c-md:c-mt-0 c-lg:c-mt-7">
            <p
              v-if="product.state === 'weather_too_hot'"
              class="font-bold text-info"
            >
              {{ t('selected_chocolate_products') }}
            </p>
            <div
              v-if="!props.product.quantityDiscounts"
              class="product-description-text relative"
            >
              <p v-html="product.description" />
              <span
                class="to-description cursor-pointer absolute right-0"
                @click="scrollToTabControl(1)"
              ><span>...</span><span class="underline font-bold">{{ t('read_more') }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { striptags } from 'striptags'
import { computed, defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import useHead from '@/mixins/headManager'
import { numberToCurrency } from '@/mixins/numberToCurrency'
import { getAllImageSizeByFormat } from '@/mixins/utils'
import { useAppStore } from '@/stores/AppStore'

const QuantityDiscountProgress = defineAsyncComponent(
  () => import('@/components/products/QuantityDiscountProgress.vue'),
)

const ProductVariantsDropdown = defineAsyncComponent(() => import('@/components/products/ProductVariantsDropdown.vue'))
const ProductStarsRating = defineAsyncComponent(() => import('@/components/products/ProductStarsRating.vue'))
const AddToCartButton = defineAsyncComponent(() => import('@/components/buttons/ButtonAddToCartButton.vue'))
const PushToMixButton = defineAsyncComponent(() => import('@/components/buttons/ButtonPushToMixButton.vue'))
const ButtonShare = defineAsyncComponent(() => import('@/components/buttons/ButtonShare.vue'))
const ButtonState = defineAsyncComponent(() => import('@/components/buttons/ButtonState.vue'))
const Favorite = defineAsyncComponent(() => import('@/components/buttons/ButtonFavorite.vue'))
const BreadCrumb = defineAsyncComponent(() => import('@/components/general/BaseBreadcrumb.vue'))
const DiscountBubble = defineAsyncComponent(() => import('@/components/general/BaseDiscountBubble.vue'))
const ProductCarousel = defineAsyncComponent(() => import('@/components/products/ProductCarousel.vue'))
const ProductPropertiesList = defineAsyncComponent(() => import('@/components/products/ProductPropertiesList.vue'))
const ProductPrice = defineAsyncComponent(() => import('@/components/products/ProductPrice.vue'))

const MixitPlusLogoSvg = defineAsyncComponent(() => import('@/components/svg/MixitPlusLogoSvg.vue'))
const MixitPlusTagSvg = defineAsyncComponent(() => import('@/components/svg/MixitPlusTagSvg.vue'))
const ProductItemToast = defineAsyncComponent(() => import('@/components/products/ProductItemToast.vue'))
const ProductEstimatedTransport = defineAsyncComponent(() => import('@/components/products/ProductEstimatedTransport.vue'))

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  productVariants: {
    type: Object,
    required: true,
  },
  depositProduct: {
    type: Array,
    required: true,
  },
  isHiddenProduct: {
    type: Boolean,
    required: true,
  },
  commentsCount: {
    type: Number,
    required: true,
  },
})

const toastObject = ref();
const createToast = (operation) => {
  toastObject.value = operation;
}

const showDiscountProgress = () => {
  if (props.product.quantityDiscounts) {
    if (props.product.plusClubPrice) {
      if (getCurrentCustomer.value?.inPlusClub) {
        return false
      }
      return true
    }
    return true
  }
  return false
}

const appStore = useAppStore()
const { getCurrentCustomer } = storeToRefs(appStore)

const sortedProductImages = computed(() => {
  return [...props.product.images].sort((a, b) => Number(b.main_image) - Number(a.main_image))
})

const emit = defineEmits(['changeActiveTab'])

const scrollToTabControl = (id) => {
  window.scrollTo({
    behavior: 'smooth',
    top: document.querySelector('#tabControl').offsetTop + document.querySelector('#main').offsetTop - 20,
  })
  emit('changeActiveTab', id)
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const url = (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.path
const description = props.product.description ? striptags(props.product.description) : ''
const urlList = computed(() => getAllImageSizeByFormat(sortedProductImages.value[0].data, 'jpeg'))
const src = computed(() => urlList.value.llg)
const categoriesPath =
  props.product.isReadyProduct || props.product.isGift
    ? router.resolve({
        name: 'productsPath',
        params: {
          permalink: props.product.categories[1].permalink,
        },
      })
    : router.resolve({
        name: 'mixesPath',
        params: {
          permalink: props.product.mixableToCategories[0].permalink,
        },
      })

const productAvailability = computed(() => {
  if (props.product.state === 'available') {
    return 'https://schema.org/InStock'
  } else if (props.product.state === 'hidden' || props.product.state === 'removed') {
    return 'https://schema.org/Discontinued'
  } else {
    return 'https://schema.org/OutOfStock'
  }
})

const productJsonld = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  'brand': {
    '@type': 'Brand',
    'name': 'Mixit',
  },
  'image': src.value,
  'name': props.product.name,
  'category':
      props.product.isReadyProduct || props.product.isGift
          ? props.product.categories[1].title
          : props.product.mixableToCategories[0].title,

  'description': props.product.shortDescription,
  'offers': {
    '@type': 'Offer',
    'price': Number(props.product.price).toFixed(2),
    'priceCurrency': props.product.currencyCode,
    url,
    'itemCondition': 'https://schema.org/NewCondition',
    'availability': productAvailability.value,
  },
}

const breadcrumbJsonld = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': t('main_page'),
      'item': baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name':
          props.product.isReadyProduct || props.product.isGift
              ? props.product.categories[1].title
              : props.product.mixableToCategories[0].title,

      'item': (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + categoriesPath.href,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': props.product.name,
    },
  ],
}

if (props.product.starRatingCommentsSum > 0) {
  productJsonld.aggregateRating = {
    "@type": "AggregateRating",
    "ratingValue": props.product.starRating,
    "ratingCount": props.product.starRatingCommentsSum,
  };
}

const jsonld = [productJsonld, breadcrumbJsonld];

useHead({
  title: props.product.name,
  meta: [
    {
      property: `og:title`,
      content: computed(() => props.product.name),
    },
    {
      property: `og:description`,
      content: description,
    },
    {
      name: `description`,
      content: description,
    },
    {
      property: `og:url`,
      content: url,
    },
    {
      property: `og:image`,
      content: src,
    },
    {
      property: `og:type`,
      content: 'product',
    },
    {
      name: 'keywords',
      content: props.product.tags || props.product.name,
    },
    // {
    //   name: 'robots',
    //   content: props.product.state === 'removed' ? 'noindex,nofollow' : 'index,follow',
    // },
  ],
  link: [
    {
      rel: 'canonical',
      href: url,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(jsonld),
    },
  ],
})

const breadcrumbList = computed(() => {
  const list = []
  if (props.product.isReadyProduct || props.product.isGift) {
    list.push({
      name: props.product.categories[1].title,
      link: { name: 'productsPath', params: { permalink: props.product.categories[1].permalink } },
    })
  } else if (props.product.isIngredient) {
    list.push({
      name: props.product.mixableToCategories[0].title,
      link: { name: 'mixesPath', params: { permalink: props.product.mixableToCategories[0].permalink } },
    })
  }
  list.push({
    name: props.product.name,
    link: { name: 'productPath', params: { permalink: props.product.permalink } },
  })
  return list
})

const productShowLink = computed(() => {
  return (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.fullPath
})

const marketingProperties = computed(() => {
  if (!props.product) return []
  return props.product.properties.filter((p) => p.propertyType === 'marketing')
})

const filteredProperties = computed(() => {
  return marketingProperties.value.filter((p) => p.systemName !== 'new' && p.systemName !== 'recommended')
})

const isNewProduct = computed(() => {
  return marketingProperties.value.some((p) => p.systemName === 'new')
})

const isRecommendedProduct = computed(() => {
  return marketingProperties.value.some((p) => p.systemName === 'recommended')
})

const ratingLabel = computed(() => {
  if (props.product.commentsSum === 1) {
    return t('ratings.one')
  } else if (props.product.commentsSum >= 2 && props.product.commentsSum <= 4) {
    return t('ratings.few')
  }
  return t('ratings.other')
})
</script>

<style lang="scss" scoped>
header {
  min-height: 80px;

  .product-breadcrumb {
    min-height: 40px;
  }
}

.product-carousel__wrapper {
  min-height: 86vw;
  height: 100%;

  @media screen and (min-width: 575px) {
    min-height: 340px;
  }

  @media screen and (min-width: 992px) {
    min-height: 500px;
  }

  @media screen and (min-width: 1200px) {
    min-height: 562px;
  }
}

.product-buttons {
  @media screen and (max-width: 1199px) {
    width: 100%;
  }
}

.to-comments {
  min-height: 24px;
}

.to-description {
  bottom: -4px;

  &:before {
    content: '';
    position: absolute;
    width: 180%;
    height: 100%;
    bottom: 0;
    right: 0;
    background-image: linear-gradient(to left, rgba(247, 231, 209, 1) 60%, rgba(247, 231, 209, 0));
  }

  span {
    z-index: 2;
    position: relative;
  }
}

.product-variants {
  height: 48px;
}

.product-description-text {
  height: 100px;
  overflow: hidden;

  @media screen and (max-width: 991px) {
    display: none;
  }
}
.mixit-plus-svg {
  width: 86px;
}
.mixit-plus-flex {
  @media screen and (max-width: 500px) {
    flex-basis: 100%;
  }
}
.line-through {
  text-decoration: line-through;
}
</style>

<i18n>
  {
    "cs": {
    "for_mixit_plus": "pro členy",
    "ingredient": "Namixuj si",
    "main_page": "Hlavní strana",
    "pack_discount_tooltip": "Sleva je vypočítána jako rozdíl oproti nákupu obsažených produktů samostatně",
    "packaging_deposit": "Cena zálohy je mimo cenu produktu",
    "ratings": {
        "few": "hodnocení",
        "one": "hodnocení",
        "other": "hodnocení"
    },
    "read_more": "Číst více",
    "selected_chocolate_products": "Jsem u ledu! Z důvodu vysokých teplot mě poslali do chládku.",
    "voucher_permalink": "mixit-plus-na-12-mesicu"
},
    "sk": {
    "for_mixit_plus": "pre členov",
    "ingredient": "Namixuj si",
    "main_page": "Hlavná stránka",
    "pack_discount_tooltip": "Zľava sa počíta ako rozdiel oproti samostatnému nákupu zahrnutých produktov",
    "packaging_deposit": "Cena zálohy je oddelená od ceny produktu.",
    "ratings": {
        "few": "hodnotenia",
        "one": "hodnotenie",
        "other": "hodnotení"
    },
    "read_more": "Prečítať si viac",
    "selected_chocolate_products": "Som v chládku! :( Čokoláda má kvôli vysokým teplotám dovolenku.",
    "voucher_permalink": "mixit-plus-na-12-mesiacov"
},
    "pl": {
    "for_mixit_plus": "dla członków",
    "ingredient": "Namixuj",
    "main_page": "Strona główna",
    "pack_discount_tooltip": "Zniżka jest obliczana jako różnica w porównaniu do zakupu poszczególnych produktów osobno.\n\n\n\n\n\n\n\n",
    "packaging_deposit": "Cena zaliczki nie jest wliczona w cenę produktu",
    "ratings": {
        "few": "oceny",
        "one": "ocena",
        "other": "ocen"
    },
    "read_more": "Czytaj więcej",
    "selected_chocolate_products": "Czekam w chłodzie! :( Ze względu na upały, czekolada została wysłana na przymusowy urlop.",
    "voucher_permalink": "mixit-plus-na-12-miesiecy"
},
    "hu": {
    "for_mixit_plus": "tagok számára",
    "ingredient": "Mixelji",
    "main_page": "Főoldal",
    "pack_discount_tooltip": "A kedvezményt a mellékelt termékek külön-külön történő megvásárlásának különbözeteként számítják ki.",
    "packaging_deposit": "Az előleg ára nem tartalmazza a termék árát.",
    "ratings": {
        "few": "értékelés",
        "one": "értékelés",
        "other": "értékelés"
    },
    "read_more": "Továbbolvasom",
    "selected_chocolate_products": "Hidegre helyezve! :( A magas hőmérsékletek miatt a csokoládés termékek szabadságon vannak.",
    "voucher_permalink": "mixit-plusz-12-honapra"
},
    "de": {
    "for_mixit_plus": "für Mitglieder",
    "ingredient": "Mix dir",
    "main_page": "Startseite",
    "pack_discount_tooltip": "Der Rabatt berechnet sich aus der Differenz zum separaten Kauf der enthaltenen Produkte.",
    "packaging_deposit": "Pfand nicht im Produktpreis enthalten.",
    "ratings": {
        "few": "Bewertung",
        "one": "Bewertung",
        "other": "Bewertung"
    },
    "read_more": "Mehr lesen",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "at": {
    "for_mixit_plus": "für Mitglieder",
    "ingredient": "Mix dir",
    "main_page": "Startseite",
    "pack_discount_tooltip": "Der Rabatt berechnet sich aus der Differenz zum separaten Kauf der enthaltenen Produkte.",
    "packaging_deposit": "Pfand nicht im Produktpreis enthalten.",
    "ratings": {
        "few": "Bewertung",
        "one": "Bewertung",
        "other": "Bewertung"
    },
    "read_more": "Mehr lesen",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "voucher_permalink": "mixit-plus-fuer-12-monate"
},
    "ro": {
    "for_mixit_plus": "pentru membri",
    "ingredient": "Amesteca",
    "main_page": "Pagina principală",
    "pack_discount_tooltip": "Reducerea este calculată ca diferență față de achiziția individuală a produselor incluse",
    "packaging_deposit": "Prețul garanției nu este inclus în prețul produsului.",
    "ratings": {
        "few": "evaluări",
        "one": "evaluare",
        "other": "evaluări"
    },
    "read_more": "mai mult",
    "selected_chocolate_products": "La rece! :( Din cauza temperaturilor înalte produsele cu ciocolată sunt în concediu.",
    "voucher_permalink": "mixit-plus-de-12-luni"
},
    "fr": {
    "for_mixit_plus": "pour les membres",
    "ingredient": "Crée ton mix",
    "main_page": "Page principale",
    "pack_discount_tooltip": "La remise est calculée comme la différence par rapport à l’achat des produits inclus séparément",
    "packaging_deposit": "Le prix de l'acompte n'est pas compris dans le prix du produit.",
    "ratings": {
        "few": "avis des clients",
        "one": "avis des clients",
        "other": "avis des clients"
    },
    "read_more": "En savoir plus",
    "selected_chocolate_products": "Je suis au frais ! À cause des températures élevées, ils m'ont plongée dans le froid.",
    "voucher_permalink": "mixit-plus-pour-12 mois"
},
    "it": {
    "for_mixit_plus": "per i membri",
    "ingredient": "Mescola",
    "main_page": "Pagina principale",
    "pack_discount_tooltip": "Lo sconto è calcolato come la differenza rispetto all’acquisto dei prodotti contenuti singolarmente.",
    "packaging_deposit": "Il prezzo del deposito non è incluso nel prezzo del prodotto",
    "ratings": {
        "few": "recensioni",
        "one": "recensioni",
        "other": "recensioni"
    },
    "read_more": "Leggi di più",
    "selected_chocolate_products": "Sono sotto ghiaccio! A causa delle alte temperature mi hanno mandato in spedizione polare per raffreddarmi.",
    "voucher_permalink": "mixit-plus-per-12-mesi"
}
  }
</i18n>
